import { clearDraftBuffer } from "../features/drafts/draft-buffer";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import i18n from "../i18n";
import { apiFetch, onSessionEnded } from "./api-client";
import { may, type Operation } from "./access";
import { applyLoupeContext, loupeContext } from "./loupe-context";
import { queryClient } from "./query-client";
import { resetActivity } from "./session-activity";
import type { SessionUser } from "./session";

/**
 * Who is signed in ([F-029], NF-008). The session is the accounts-rbac
 * module's HttpOnly cookie; this context only asks the tool's `GET /me` who
 * it belongs to, and drops to `anonymous` the moment any request finds the
 * session gone (a 401 - expired, idle, revoked, the account retired).
 *
 *   - load       -> GET /me: 200 is a session, 401 is none.
 *   - login      -> the login screen calls `refresh()` once the module has set
 *                   the cookie.
 *   - logout     -> POST /access/logout, every cached query dropped, so the
 *                   next person at the same device starts clean ([AC-051]).
 *
 * Roles are for display; `can` decides what the surface offers, and the
 * server re-checks every request against the rights matrix ([F-027]).
 */

type Status = "loading" | "authenticated" | "anonymous";

/** Why the person is anonymous - the login screen says it. */
export type AnonymousReason = "none" | "ended" | "logout";

interface AuthState {
  status: Status;
  user: SessionUser | null;
  /** Set when a running session ended under the person (not at load). */
  reason: AnonymousReason;
  /** Re-read `/me` - after the login, and after a self-service change. */
  refresh: () => Promise<boolean>;
  logout: () => void;
  /**
   * True when the user holds the given tool-internal role. For the
   * administration only (F-027: accounts and roles are the admin role's, not
   * an operation) and GR-007's Admin-Korrektur; every content right is `can`.
   */
  hasRole: (role: string) => boolean;
  /**
   * May the user perform this operation of the rights matrix somewhere
   * ([F-027], /me.can)? Meant for operations without an object; an operation
   * on an incident or a task is asked of that object's `can`.
   */
  can: (op: Operation) => boolean;
  /** The system account ([F-031]): administration only, no content. */
  isSystem: boolean;
  /** Replace the signed-in identity after a self-service change. */
  updateUser: (user: SessionUser) => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

/** GET /me: the identity, or null when there is no session. */
async function loadMe(): Promise<SessionUser | null> {
  try {
    return await apiFetch<SessionUser>("/me", undefined, { anonymous: true });
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUser] = useState<SessionUser | null>(null);
  const [reason, setReason] = useState<AnonymousReason>("none");
  const statusRef = useRef<Status>("loading");
  statusRef.current = status;

  const refresh = useCallback(async () => {
    const me = await loadMe();
    setUser(me);
    setStatus(me ? "authenticated" : "anonymous");
    if (me) setReason("none");
    return me !== null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    void loadMe().then((me) => {
      if (cancelled) return;
      setUser(me);
      setStatus(me ? "authenticated" : "anonymous");
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Any request that finds the session gone ends it here, once.
  useEffect(
    () =>
      onSessionEnded(() => {
        if (statusRef.current !== "authenticated") return;
        statusRef.current = "anonymous";
        queryClient.clear();
        setUser(null);
        setReason("ended");
        setStatus("anonymous");
      }),
    [],
  );

  const logout = useCallback(() => {
    void (async () => {
      try {
        await apiFetch("/access/logout", { method: "POST" }, { anonymous: true });
      } catch {
        /* clear locally regardless */
      }
      queryClient.clear();
      // NF-022's volatile buffer holds this person's unsaved input; the next
      // person at this browser must not be offered it.
      clearDraftBuffer();
      resetActivity();
      setUser(null);
      setReason("logout");
      setStatus("anonymous");
    })();
  }, []);

  // A Loupe entry records the account and roles it was captured under (loupe-context.ts).
  useEffect(() => applyLoupeContext(document, loupeContext(user, (key) => i18n.t(key))), [user]);

  const hasRole = useCallback((role: string) => user?.roles.includes(role) ?? false, [user]);
  const can = useCallback((op: Operation) => may(user, op), [user]);
  const updateUser = useCallback((next: SessionUser) => setUser(next), []);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      reason,
      refresh,
      logout,
      hasRole,
      can,
      isSystem: user?.isSystem === true,
      updateUser,
    }),
    [status, user, reason, refresh, logout, hasRole, can, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
